import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-toastify';
import InputMask from 'react-input-mask';
import logoAccess from '../../images/logoaccess.png';
import './styles.css';

const STORAGE_KEY = 'satisfacao_token';
const STORAGE_EVENTO = 'satisfacao_idEvento';

// Dado que identifica o participante no login, escolhido na pesquisa (evento.login,
// coluna loginsatisfacao). `texto` é a chave da instrução no dicionário UI.
const FORMAS_LOGIN = {
  email: { texto: 'informeEmail', placeholder: 'seu@email.com', autoComplete: 'email' },
  celular: { texto: 'informeCelular', placeholder: '(11) 99999-9999', mask: '(99) 99999-9999', autoComplete: 'tel' },
  cpf: { texto: 'informeCpf', placeholder: '000.000.000-00', mask: '999.999.999-99', autoComplete: 'off' },
};

/**
 * Cor da nota no mapa de calor, pela posição RELATIVA dela na escala.
 * Relativa, e não pelo número absoluto: o operador cadastra a faixa que quiser
 * (0–10, 1–5, 0–3), e em todas o extremo baixo tem que sair vermelho e o alto
 * verde. Fixar por número faria uma escala 1–5 ficar inteira avermelhada.
 */
/**
 * Estimativa honesta de duração: ~4s por pergunta (toque numa nota). O texto era
 * fixo "leva menos de um minuto" — verdade com 3 perguntas, mentira com as 58 do
 * modelo ATALAC. Prometer 1 minuto e tomar 4 é o que faz a pessoa abandonar.
 */
// ── Textos fixos da interface, por idioma ─────────────────────────────────
// O CONTEÚDO (perguntas/opções) vem traduzido do banco; isto aqui é só a
// moldura da tela. Dicionário local de propósito: viaja com o código, não
// depende de nada carregar antes do primeiro paint.
const UI = {
  portugues: {
    titulo: 'Pesquisa de Satisfação',
    informeEmail: 'Informe o mesmo e-mail que está na lista de participantes do evento.',
    informeCelular: 'Informe o mesmo celular que está na lista de participantes do evento.',
    informeCpf: 'Informe o mesmo CPF que está na lista de participantes do evento.',
    entrar: 'Entrar', entrando: 'Entrando...',
    ola: (nome) => `Olá, ${nome}!`,
    intro: 'Sua opinião nos ajuda a melhorar os próximos eventos.',
    carregandoPerguntas: ' Carregando as perguntas...',
    resumo: (n, t) => ` São ${n} pergunta${n > 1 ? 's' : ''} — ${t}.`,
    semPerguntas: ' Não há perguntas cadastradas para este evento.',
    tempoMenos1: 'leva menos de um minuto', tempoCerca: (m) => `leva cerca de ${m} minutos`,
    comecar: 'Começar', carregando: 'Carregando...',
    perguntaXdeY: (x, y) => <span>Pergunta <b>{x}</b> de <b>{y}</b></span>,
    respondidas: (n) => `${n} respondida${n === 1 ? '' : 's'}`,
    muitoInsatisfeito: 'Muito insatisfeito', muitoSatisfeito: 'Muito satisfeito',
    naoParticipei: 'Não participei',
    escrevaAqui: 'Escreva aqui... (opcional)',
    avancar: 'Avançar', pularPergunta: 'Pular esta pergunta',
    outro: 'Outro...', qual: 'Qual?', confirmar: 'Confirmar',
    nenhumaAvancar: 'Nenhuma das opções — avançar',
    enviando: 'Enviando suas respostas...',
    obrigado: 'Obrigado pela participação!',
    registradas: 'Suas respostas foram registradas. Sua opinião é muito importante para nós.',
    jaRespondeu: 'Você já havia respondido esta pesquisa — não é possível enviar de novo.',
    acessando: 'Acessando a pesquisa...',
  },
  espanhol: {
    titulo: 'Encuesta de Satisfacción',
    informeEmail: 'Ingrese el mismo correo que figura en la lista de participantes del evento.',
    informeCelular: 'Ingrese el mismo celular que figura en la lista de participantes del evento.',
    informeCpf: 'Ingrese el mismo CPF que figura en la lista de participantes del evento.',
    entrar: 'Entrar', entrando: 'Entrando...',
    ola: (nome) => `¡Hola, ${nome}!`,
    intro: 'Su opinión nos ayuda a mejorar los próximos eventos.',
    carregandoPerguntas: ' Cargando las preguntas...',
    resumo: (n, t) => ` Son ${n} pregunta${n > 1 ? 's' : ''} — ${t}.`,
    semPerguntas: ' No hay preguntas registradas para este evento.',
    tempoMenos1: 'toma menos de un minuto', tempoCerca: (m) => `toma cerca de ${m} minutos`,
    comecar: 'Comenzar', carregando: 'Cargando...',
    perguntaXdeY: (x, y) => <span>Pregunta <b>{x}</b> de <b>{y}</b></span>,
    respondidas: (n) => `${n} respondida${n === 1 ? '' : 's'}`,
    muitoInsatisfeito: 'Muy insatisfecho', muitoSatisfeito: 'Muy satisfecho',
    naoParticipei: 'No participé',
    escrevaAqui: 'Escriba aquí... (opcional)',
    avancar: 'Avanzar', pularPergunta: 'Omitir esta pregunta',
    outro: 'Otro...', qual: '¿Cuál?', confirmar: 'Confirmar',
    nenhumaAvancar: 'Ninguna de las opciones — avanzar',
    enviando: 'Enviando sus respuestas...',
    obrigado: '¡Gracias por participar!',
    registradas: 'Sus respuestas fueron registradas. Su opinión es muy importante para nosotros.',
    jaRespondeu: 'Usted ya había respondido esta encuesta — no es posible enviarla de nuevo.',
    acessando: 'Accediendo a la encuesta...',
  },
  ingles: {
    titulo: 'Satisfaction Survey',
    informeEmail: 'Enter the same e-mail that is on the event participant list.',
    informeCelular: 'Enter the same mobile number that is on the event participant list.',
    informeCpf: 'Enter the same CPF that is on the event participant list.',
    entrar: 'Sign in', entrando: 'Signing in...',
    ola: (nome) => `Hi, ${nome}!`,
    intro: 'Your feedback helps us improve future events.',
    carregandoPerguntas: ' Loading the questions...',
    resumo: (n, t) => ` There ${n > 1 ? 'are' : 'is'} ${n} question${n > 1 ? 's' : ''} — ${t}.`,
    semPerguntas: ' There are no questions registered for this event.',
    tempoMenos1: 'it takes less than a minute', tempoCerca: (m) => `it takes about ${m} minutes`,
    comecar: 'Start', carregando: 'Loading...',
    perguntaXdeY: (x, y) => <span>Question <b>{x}</b> of <b>{y}</b></span>,
    respondidas: (n) => `${n} answered`,
    muitoInsatisfeito: 'Very dissatisfied', muitoSatisfeito: 'Very satisfied',
    naoParticipei: "I didn't take part",
    escrevaAqui: 'Write here... (optional)',
    avancar: 'Next', pularPergunta: 'Skip this question',
    outro: 'Other...', qual: 'Which?', confirmar: 'Confirm',
    nenhumaAvancar: 'None of the options — next',
    enviando: 'Sending your answers...',
    obrigado: 'Thank you for taking part!',
    registradas: 'Your answers have been recorded. Your feedback matters a lot to us.',
    jaRespondeu: 'You have already answered this survey — it cannot be sent again.',
    acessando: 'Opening the survey...',
  },
  frances: {
    titulo: 'Enquête de Satisfaction',
    informeEmail: "Saisissez le même e-mail que celui de la liste des participants de l'événement.",
    informeCelular: "Saisissez le même numéro de portable que celui de la liste des participants de l'événement.",
    informeCpf: "Saisissez le même CPF que celui de la liste des participants de l'événement.",
    entrar: 'Entrer', entrando: 'Connexion...',
    ola: (nome) => `Bonjour, ${nome} !`,
    intro: 'Votre avis nous aide à améliorer les prochains événements.',
    carregandoPerguntas: ' Chargement des questions...',
    resumo: (n, t) => ` Il y a ${n} question${n > 1 ? 's' : ''} — ${t}.`,
    semPerguntas: " Aucune question n'est enregistrée pour cet événement.",
    tempoMenos1: 'cela prend moins d’une minute', tempoCerca: (m) => `cela prend environ ${m} minutes`,
    comecar: 'Commencer', carregando: 'Chargement...',
    perguntaXdeY: (x, y) => <span>Question <b>{x}</b> sur <b>{y}</b></span>,
    respondidas: (n) => `${n} répondue${n === 1 ? '' : 's'}`,
    muitoInsatisfeito: 'Très insatisfait', muitoSatisfeito: 'Très satisfait',
    naoParticipei: "Je n'ai pas participé",
    escrevaAqui: 'Écrivez ici... (facultatif)',
    avancar: 'Suivant', pularPergunta: 'Passer cette question',
    outro: 'Autre...', qual: 'Lequel ?', confirmar: 'Confirmer',
    nenhumaAvancar: 'Aucune des options — suivant',
    enviando: 'Envoi de vos réponses...',
    obrigado: 'Merci de votre participation !',
    registradas: 'Vos réponses ont été enregistrées. Votre avis compte beaucoup pour nous.',
    jaRespondeu: 'Vous avez déjà répondu à cette enquête — impossible de la renvoyer.',
    acessando: "Ouverture de l'enquête...",
  },
};

function estimativaTempo(total, ui) {
  const min = Math.ceil((total * 4) / 60);
  return min <= 1 ? ui.tempoMenos1 : ui.tempoCerca(min);
}

function corDaNota(valor, min, max) {
  const t = max === min ? 1 : (valor - min) / (max - min);
  // vermelho (0°) → âmbar (~45°) → verde (~140°)
  const hue = t <= 0.5 ? 4 + t * 2 * 42 : 46 + (t - 0.5) * 2 * 94;
  const sat = t <= 0.5 ? 72 : 62;
  const luz = t <= 0.5 ? 50 : 38;
  return `hsl(${Math.round(hue)}, ${sat}%, ${luz}%)`;
}

export default function PerguntasSatisfacao() {
  const { idEvento, idParticipante } = useParams();
  const [step, setStep] = useState('login'); // login | welcome | pergunta | obrigado
  const [valorLogin, setValorLogin] = useState('');
  const [formaLogin, setFormaLogin] = useState(null); // null até o /satisfacao/evento responder
  const [loading, setLoading] = useState(false);
  const [loadingAcessoDireto, setLoadingAcessoDireto] = useState(!!idParticipante);
  const [error, setError] = useState('');
  const [token, setToken] = useState(() => (idParticipante ? null : localStorage.getItem(STORAGE_KEY)));
  const [participante, setParticipante] = useState(null);
  const [evento, setEvento] = useState(null);
  const [perguntas, setPerguntas] = useState([]);
  const [respostas, setRespostas] = useState({});
  const [indicePergunta, setIndicePergunta] = useState(0);
  const [notaSelecionadaAtual, setNotaSelecionadaAtual] = useState(null); // limpa ao trocar de pergunta
  const [opcaoSelecionadaAtual, setOpcaoSelecionadaAtual] = useState(null); // escolha única, idem
  const [textoAtual, setTextoAtual] = useState('');
  const [marcadasAtual, setMarcadasAtual] = useState([]); // múltipla escolha
  const [outroAberto, setOutroAberto] = useState(false);  // escolha única -> "Outro"
  const [outroTexto, setOutroTexto] = useState('');
  const [jaRespondido, setJaRespondido] = useState(false);
  const [mostrarAvisoJaRespondido, setMostrarAvisoJaRespondido] = useState(false);
  const [loadingPerguntas, setLoadingPerguntas] = useState(false);
  // Idioma escolhido pelo participante. Só português até o evento oferecer
  // outros (evento.idiomas vem do público /satisfacao/evento).
  const [idioma, setIdioma] = useState('portugues');
  const ui = UI[idioma] || UI.portugues;
  const forma = FORMAS_LOGIN[formaLogin] || FORMAS_LOGIN.email;
  const idiomasDoEvento = (evento && Array.isArray(evento.idiomas)) ? evento.idiomas : [];

  const perguntaAtual = perguntas[indicePergunta];

  // Limpa a resposta em andamento ao trocar de pergunta. useLayoutEffect porque roda antes de a
  // tela ser desenhada: com useEffect (React 17), a pergunta nova podia mostrar por um quadro a
  // resposta da anterior (nota, opções marcadas, texto, "Outro").
  useLayoutEffect(() => {
    setNotaSelecionadaAtual(null);
    setOpcaoSelecionadaAtual(null);
    setTextoAtual('');
    setMarcadasAtual([]);
    setOutroAberto(false);
    setOutroTexto('');
  }, [indicePergunta]);
  const totalPerguntas = perguntas.length;
  const isUltimaPergunta = indicePergunta >= totalPerguntas - 1;

  // Acesso direto por link com id do participante: obtém token sem login por e-mail
  useEffect(() => {
    if (!idEvento || !idParticipante) return;
    setLoadingAcessoDireto(true);
    api({
      method: 'get',
      action: `/satisfacao/acessoDireto/${idEvento}/${idParticipante}`,
    })
      .then(res => {
        const data = res.data?.data ?? res.data;
        const t = data?.token;
        if (t) {
          localStorage.setItem(STORAGE_KEY, t);
          localStorage.setItem(STORAGE_EVENTO, idEvento);
          setToken(t);
          setParticipante(data?.participante ?? {});
          setEvento(data?.evento ?? {});
          setStep('welcome');
        } else {
          setError('Não foi possível acessar. Use o link completo enviado por e-mail.');
        }
      })
      .catch(() => {
        setError('Link inválido ou expirado. Se preferir, acesse pelo formulário abaixo.');
      })
      .finally(() => setLoadingAcessoDireto(false));
  }, [idEvento, idParticipante]);

  // Identidade visual (nome/banner/logo) ANTES de qualquer autenticação: quem
  // entra pelo fluxo de e-mail veria uma tela de login sem marca nenhuma, sem
  // saber de que evento é a pesquisa. Não sobrescreve o que o login/acessoDireto
  // já trouxer — aquilo é a fonte mais completa.
  useEffect(() => {
    if (!idEvento) return;
    api({ method: 'get', action: `/satisfacao/evento/${idEvento}` })
      .then(res => {
        const data = res.data?.data ?? res.data;
        if (data && data.id) setEvento(prev => (prev && prev.id) ? prev : data);
        setFormaLogin(data?.login || 'email');
      })
      .catch(() => setFormaLogin('email')); // sem resposta, segue o login por e-mail de sempre
  }, [idEvento]);

  useEffect(() => {
    if (!idEvento) return;
    if (idParticipante) return; // já tratado no efeito de acesso direto
    const storedEvento = localStorage.getItem(STORAGE_EVENTO);
    if (storedEvento !== idEvento) {
      localStorage.removeItem(STORAGE_KEY);
      setToken(null);
    }
    localStorage.setItem(STORAGE_EVENTO, idEvento);
  }, [idEvento, idParticipante]);

  useEffect(() => {
    if (!token || !idEvento) return;
    api({
      method: 'get',
      action: `/satisfacao/jaRespondido/${idEvento}`,
      token
    })
      .then(res => {
        const data = res.data?.data ?? res.data;
        if (data?.jaRespondido) {
          setJaRespondido(true);
          setStep('obrigado');
        }
      })
      .catch(() => {});
  }, [token, idEvento]);

  useEffect(() => {
    if (!token || !idEvento || step !== 'welcome') return;
    setLoadingPerguntas(true);
    api({
      method: 'get',
      action: `/satisfacao/perguntas/${idEvento}` + (idioma !== 'portugues' ? `?idioma=${idioma}` : ''),
      token
    })
      .then(res => {
        const data = res.data?.data ?? res.data;
        const list = data?.perguntas ?? data ?? [];
        setPerguntas(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        toast.error('Não foi possível carregar as perguntas.');
        setStep('login');
      })
      .finally(() => setLoadingPerguntas(false));
  }, [token, idEvento, step, idioma]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await api({
        method: 'post',
        action: '/satisfacao/login',
        // A chave segue `email`; com celular ou CPF ela leva esse dado (SatisfacaoPublicaController::login)
        data: { email: valorLogin.trim(), idEvento }
      });
      const data = response.data?.data ?? response.data;
      const t = data?.token ?? data?.access_token;
      if (!t) {
        setError('Resposta inválida. Tente novamente.');
        return;
      }
      localStorage.setItem(STORAGE_KEY, t);
      setToken(t);
      setParticipante(data?.participante ?? {});
      setEvento(data?.evento ?? {});
      setStep('welcome');
    } catch (err) {
      const msg = err.response?.data?.error ?? err.response?.data?.message ?? 'Não encontramos você na lista de participantes deste evento.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleContinuarWelcome = () => {
    if (perguntas.length === 0) {
      toast.info('Não há perguntas cadastradas para este evento.');
      return;
    }
    setStep('pergunta');
    setIndicePergunta(0);
  };

  // Resposta agora é um OBJETO por pergunta ({nota} | {naoParticipei} | {texto} |
  // {textos: []}), não mais o número cru: a pesquisa tem 4 tipos de pergunta e o
  // payload precisa dizer qual formato está indo.
  const registrarResposta = (resposta) => {
    if (!perguntaAtual) return;
    const novaRespostas = { ...respostas, [perguntaAtual.id]: resposta };
    setRespostas(novaRespostas);

    // Próxima pergunta VISÍVEL considerando a resposta que acabou de entrar —
    // é ela que pode ter aberto ("avalie o SENAI") ou fechado (encerra seção)
    // perguntas à frente. Sem próxima visível = fim da pesquisa.
    const marcada = (r, opcao) => r && (Array.isArray(r.textos) ? r.textos.includes(opcao) : r.texto === opcao);
    const visivelCom = (q, idx) => {
      if (q.condicaoPerguntaId) {
        const fonte = perguntas.find(p => String(p.id) === String(q.condicaoPerguntaId));
        if (fonte && !marcada(novaRespostas[fonte.id], q.condicaoOpcao)) return false;
      }
      for (let i = 0; i < idx; i++) {
        const p = perguntas[i];
        const enc = p.opcoes && p.opcoes.encerra_secao;
        if (enc && p.secao === q.secao && marcada(novaRespostas[p.id], enc)) return false;
      }
      return true;
    };
    let proxima = -1;
    for (let i = indicePergunta + 1; i < perguntas.length; i++) {
      if (visivelCom(perguntas[i], i)) { proxima = i; break; }
    }
    if (proxima === -1) {
      enviarRespostas(novaRespostas);
    } else {
      setIndicePergunta(proxima); // ao avançar, o useLayoutEffect limpa os estados da pergunta
    }
  };

  const enviarRespostas = async (todasRespostas) => {
    const lista = Object.entries(todasRespostas).map(([perguntaId, r]) => ({ perguntaId, ...r }));
    setLoading(true);
    try {
      await api({
        method: 'post',
        action: `/satisfacao/respostas/${idEvento}`,
        token,
        data: { respostas: lista }
      });
      setStep('obrigado');
      setJaRespondido(true);
    } catch (err) {
      const msg = err.response?.data?.error ?? 'Não foi possível enviar. Tente novamente.';
      const jaRespondido = err.response?.data?.jaRespondido === true || err.response?.status === 409;
      if (jaRespondido) {
        toast.info(msg);
        setMostrarAvisoJaRespondido(true);
        setStep('obrigado');
        setJaRespondido(true);
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const notaMin = perguntaAtual ? (perguntaAtual.notaMin ?? 0) : 0;
  const notaMax = perguntaAtual ? (perguntaAtual.notaMax ?? 10) : 10;
  const tipoAtual = perguntaAtual ? (perguntaAtual.tipoResposta || 'nota') : 'nota';
  const itensOpcao = (perguntaAtual && perguntaAtual.opcoes && Array.isArray(perguntaAtual.opcoes.itens)) ? perguntaAtual.opcoes.itens : [];
  // Texto no idioma escolhido, pareado por posição com o canônico (pt). A tela
  // MOSTRA a tradução e GRAVA o canônico — é o que mantém condicionais,
  // encerra_secao e o relatório funcionando em qualquer idioma.
  const itensExibicao = (perguntaAtual && perguntaAtual.opcoes && Array.isArray(perguntaAtual.opcoes.itens_exibicao))
    ? perguntaAtual.opcoes.itens_exibicao : itensOpcao;
  const exibicaoDe = (op) => {
    const i = itensOpcao.indexOf(op);
    return (i >= 0 && itensExibicao[i]) ? itensExibicao[i] : op;
  };
  const permiteOutro = !!(perguntaAtual && perguntaAtual.opcoes && perguntaAtual.opcoes.permite_outro);
  const opcoes = [];
  for (let n = notaMin; n <= notaMax; n++) opcoes.push(n);

  // Colunas da escala: até 6 notas cabem numa linha só; acima disso, divide em
  // DUAS linhas equilibradas (0–10 vira 6+5). Deixar o navegador quebrar sozinho
  // produzia 9 numa linha e 2 na outra — foi esse desalinhamento que deu a
  // impressão de "números jogados".
  const colunasEscala = opcoes.length <= 6 ? opcoes.length : Math.ceil(opcoes.length / 2);

  // ── Visibilidade condicional ────────────────────────────────────────────
  // Duas regras, avaliadas contra as respostas já dadas:
  // 1. condicaoPerguntaId/condicaoOpcao: só aparece se a opção foi marcada na
  //    pergunta-fonte ("avalie o SENAI" só para quem marcou SENAI).
  // 2. opcoes.encerra_secao: quem escolheu a opção marcada como "encerra a
  //    seção" (ex.: "Não participei das visitas externas") pula todas as
  //    perguntas SEGUINTES da mesma seção.
  const opcaoMarcada = (resposta, opcao) => {
    if (!resposta) return false;
    if (Array.isArray(resposta.textos)) return resposta.textos.includes(opcao);
    return resposta.texto === opcao;
  };
  const perguntaVisivel = (q, idx) => {
    if (q.condicaoPerguntaId) {
      const fonte = perguntas.find(p => String(p.id) === String(q.condicaoPerguntaId));
      if (fonte && !opcaoMarcada(respostas[fonte.id], q.condicaoOpcao)) return false;
    }
    for (let i = 0; i < idx; i++) {
      const p = perguntas[i];
      const encerra = p.opcoes && p.opcoes.encerra_secao;
      if (encerra && p.secao === q.secao && opcaoMarcada(respostas[p.id], encerra)) return false;
    }
    return true;
  };
  const visiveis = perguntas
    .map((q, idx) => ({ q, idx }))
    .filter(({ q, idx }) => perguntaVisivel(q, idx));

  const respondidas = Object.keys(respostas).length;
  const totalVisiveis = visiveis.length;
  const posicaoAtual = visiveis.findIndex(v => v.idx === indicePergunta) + 1;
  const pctProgresso = totalVisiveis > 0
    ? Math.round((respondidas / totalVisiveis) * 100)
    : 0;

  // Banner com a PROPORÇÃO NATURAL da imagem (img, não background-cover): a arte
  // que o operador sobe aparece inteira, sem esticar nem cortar — pedido do
  // Rafael (25/08): "o banner não pode distorcer".
  // ocultarMarca = banner já vem todo personalizado: sem logo, sem nome e SEM o
  // véu escuro (o véu só existe para dar contraste à marca sobreposta).
  const ocultarMarca = !!evento?.ocultarMarca;
  const banner = (
    <div className={'ps__banner' + (evento?.imagemFundo ? ' has-img' : '') + (ocultarMarca ? ' sem-marca' : '')}>
      {evento?.imagemFundo && (
        <img className="ps__banner-img" src={evento.imagemFundo} alt="" aria-hidden="true" />
      )}
      {!ocultarMarca && (
        <div className="ps__banner-marca">
          {evento?.logo && (
            <img className="ps__banner-logo" src={evento.logo} alt={evento?.nome || 'Logo do cliente'} />
          )}
          {/* Nome SEMPRE abaixo do logo. Sem logo cadastrado, o nome cresce e
              ocupa o lugar sozinho, para o banner nunca ficar vazio. */}
          {evento?.nome && (
            <span className={'ps__banner-nome' + (evento?.logo ? ' is-sob-logo' : '')}>
              {evento.nome}
            </span>
          )}
        </div>
      )}
    </div>
  );

  if (!idEvento) {
    return (
      <main className="ps">
        {banner}
        <div className="ps__wrap">
          <div className="ps__card ps__final">
            <p className="ps__sub" style={{ marginBottom: 0 }}>
              Link inválido. Use o link enviado para a pesquisa.
            </p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="ps">
      {banner}

      <div className="ps__wrap">
        <div className="ps__card">

          {idiomasDoEvento.length > 1 && (step === 'login' || step === 'welcome') && (
            <div className="ps__idiomas">
              {idiomasDoEvento.map(i => (
                <button key={i.codigo} type="button"
                  className={'ps__idioma' + (idioma === i.codigo ? ' is-sel' : '')}
                  onClick={() => setIdioma(i.codigo)}>
                  {i.nome}
                </button>
              ))}
            </div>
          )}

          {step === 'login' && (
            loadingAcessoDireto || !formaLogin ? (
              <div className="ps__final">
                <div className="ps__skel" style={{ width: '58%' }} />
                <div className="ps__skel" style={{ width: '38%' }} />
                <p className="ps__sub" style={{ margin: '14px 0 0' }}>{ui.acessando}</p>
              </div>
            ) : (
              <>
                <h1 className="ps__titulo">{ui.titulo}</h1>
                <p className="ps__sub">{ui[forma.texto]}</p>
                <form onSubmit={handleLogin}>
                  {error && <div className="ps__erro">{error}</div>}
                  {forma.mask ? (
                    <InputMask
                      mask={forma.mask}
                      maskChar={null}
                      type="tel"
                      className="ps__campo"
                      placeholder={forma.placeholder}
                      value={valorLogin}
                      onChange={e => setValorLogin(e.target.value)}
                      autoComplete={forma.autoComplete}
                      required
                      style={{ marginBottom: 12 }}
                    />
                  ) : (
                    <input
                      type="email"
                      className="ps__campo"
                      placeholder={forma.placeholder}
                      value={valorLogin}
                      onChange={e => setValorLogin(e.target.value)}
                      autoComplete={forma.autoComplete}
                      required
                      style={{ marginBottom: 12 }}
                    />
                  )}
                  <button type="submit" className="ps__btn" disabled={loading}>
                    {loading ? ui.entrando : ui.entrar}
                  </button>
                </form>
              </>
            )
          )}

          {step === 'welcome' && (
            <div className="ps__final">
              <h1 className="ps__titulo">
                {ui.ola((participante?.nome || 'participante').split(' ')[0])}
              </h1>
              <p className="ps__sub">
                {ui.intro}
                {loadingPerguntas
                  ? ui.carregandoPerguntas
                  : totalPerguntas > 0
                    ? ui.resumo(totalPerguntas, estimativaTempo(totalPerguntas, ui))
                    : ui.semPerguntas}
              </p>
              <button
                type="button"
                className="ps__btn"
                onClick={handleContinuarWelcome}
                disabled={loadingPerguntas || totalPerguntas === 0}
              >
                {loadingPerguntas ? ui.carregando : ui.comecar}
              </button>
            </div>
          )}

          {step === 'pergunta' && perguntaAtual && (
            <>
              <div className="ps__progresso">
                <div className="ps__progresso-topo">
                  {ui.perguntaXdeY(posicaoAtual > 0 ? posicaoAtual : indicePergunta + 1, totalVisiveis)}
                  <span>{ui.respondidas(respondidas)}</span>
                </div>
                <div className="ps__barra">
                  <div className="ps__barra-fill" style={{ width: pctProgresso + '%' }} />
                </div>
                {/* Bolinhas só com poucas perguntas — com muitas viram ruído visual. */}
                {totalVisiveis <= 12 && (
                  <div className="ps__passos">
                    {visiveis.map(({ q, idx }) => (
                      <span
                        key={q.id ?? idx}
                        className={'ps__passo' + (respostas[q.id] != null ? ' is-feita' : (idx === indicePergunta ? ' is-atual' : ''))}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Cabeçalho da seção — orienta onde a pessoa está na pesquisa
                  (Perfil, Organização, Visitas externas...). */}
              {perguntaAtual.secao && <p className="ps__secao">{perguntaAtual.secao}</p>}

              <h2 className="ps__pergunta">{perguntaAtual.pergunta}</h2>
              {perguntaAtual.instrucao && <p className="ps__instrucao">{perguntaAtual.instrucao}</p>}

              {/* ── NOTA (escala em mapa de calor) ── */}
              {tipoAtual === 'nota' && (<>
                <div className="ps__escala" style={{ '--ps-cols': colunasEscala }}>
                  {opcoes.map(n => {
                    const selecionada = notaSelecionadaAtual === n;
                    return (
                      <button
                        key={n}
                        type="button"
                        className={'ps__nota' + (selecionada ? ' is-sel' : '')}
                        style={{ background: corDaNota(n, notaMin, notaMax) }}
                        onClick={() => setNotaSelecionadaAtual(n)}
                        disabled={loading}
                        aria-label={`Nota ${n} de ${notaMax}`}
                      >
                        {n}
                      </button>
                    );
                  })}
                </div>
                <div className="ps__escala-pes">
                  <span>{notaMin} · {ui.muitoInsatisfeito}</span>
                  <span>{ui.muitoSatisfeito} · {notaMax}</span>
                </div>
                {/* Fiel ao papel: cada item de visita oferece a saída, sem pular pergunta. */}
                {perguntaAtual.permiteNaoParticipei && (
                  <button type="button" className="ps__nao-participei" disabled={loading}
                    onClick={() => registrarResposta({ naoParticipei: true })}>
                    {ui.naoParticipei}
                  </button>
                )}
                {/* Clicar na nota só marca; gravar e avançar fica no botão, como nos outros tipos. */}
                <button type="button" className="ps__btn" style={{ marginTop: 12 }}
                  disabled={loading || notaSelecionadaAtual === null}
                  onClick={() => registrarResposta({ nota: notaSelecionadaAtual })}>
                  {ui.avancar}
                </button>
              </>)}

              {/* ── TEXTO LIVRE ── */}
              {tipoAtual === 'texto' && (<>
                <textarea
                  className="ps__campo ps__textarea"
                  rows={4}
                  placeholder={ui.escrevaAqui}
                  value={textoAtual}
                  onChange={e => setTextoAtual(e.target.value)}
                  disabled={loading}
                />
                {/* Papel permite deixar em branco — o botão avança mesmo vazio. */}
                <button type="button" className="ps__btn" style={{ marginTop: 12 }} disabled={loading}
                  onClick={() => registrarResposta({ texto: textoAtual.trim() })}>
                  {textoAtual.trim() ? ui.avancar : ui.pularPergunta}
                </button>
              </>)}

              {/* ── ESCOLHA ÚNICA — clicar marca, Avançar grava; "Outro" abre campo ── */}
              {tipoAtual === 'escolha_unica' && (<>
                <div className="ps__opcoes">
                  {itensOpcao.map(op => (
                    <button key={op} type="button" disabled={loading}
                      className={'ps__opcao' + (opcaoSelecionadaAtual === op ? ' is-marcada' : '')}
                      onClick={() => { setOpcaoSelecionadaAtual(op); setOutroAberto(false); }}>
                      {exibicaoDe(op)}
                    </button>
                  ))}
                  {permiteOutro && !outroAberto && (
                    <button type="button" className="ps__opcao" disabled={loading}
                      onClick={() => { setOutroAberto(true); setOpcaoSelecionadaAtual(null); }}>
                      {ui.outro}
                    </button>
                  )}
                  {permiteOutro && outroAberto && (
                    <div className="ps__outro">
                      <input className="ps__campo" autoFocus placeholder={ui.qual}
                        value={outroTexto} onChange={e => setOutroTexto(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && outroTexto.trim()) registrarResposta({ texto: 'Outro: ' + outroTexto.trim() }); }} />
                      <button type="button" className="ps__btn" disabled={loading || !outroTexto.trim()}
                        onClick={() => registrarResposta({ texto: 'Outro: ' + outroTexto.trim() })}>
                        {ui.confirmar}
                      </button>
                    </div>
                  )}
                </div>
                {/* Com "Outro" aberto, quem grava é o Confirmar do campo. */}
                {!outroAberto && (
                  <button type="button" className="ps__btn" style={{ marginTop: 12 }}
                    disabled={loading || opcaoSelecionadaAtual === null}
                    onClick={() => registrarResposta({ texto: opcaoSelecionadaAtual })}>
                    {ui.avancar}
                  </button>
                )}
              </>)}

              {/* ── MÚLTIPLA ESCOLHA — marca quantas quiser e avança ── */}
              {tipoAtual === 'multipla_escolha' && (<>
                <div className="ps__opcoes">
                  {itensOpcao.map(op => {
                    const marcada = marcadasAtual.includes(op);
                    return (
                      <button key={op} type="button" disabled={loading}
                        className={'ps__opcao is-check' + (marcada ? ' is-marcada' : '')}
                        onClick={() => setMarcadasAtual(prev => marcada ? prev.filter(x => x !== op) : [...prev, op])}>
                        <span className="ps__opcao-box">{marcada ? '✓' : ''}</span>
                        {exibicaoDe(op)}
                      </button>
                    );
                  })}
                </div>
                <button type="button" className="ps__btn" style={{ marginTop: 12 }} disabled={loading}
                  onClick={() => registrarResposta({ textos: marcadasAtual })}>
                  {marcadasAtual.length > 0 ? ui.avancar : ui.nenhumaAvancar}
                </button>
              </>)}

              {loading && <p className="ps__sub" style={{ margin: '18px 0 0' }}>{ui.enviando}</p>}
            </>
          )}

          {step === 'obrigado' && (
            <div className="ps__final">
              <div className="ps__check">✓</div>
              <h1 className="ps__titulo">{ui.obrigado}</h1>
              <p className="ps__sub" style={{ marginBottom: 0 }}>
                {mostrarAvisoJaRespondido ? ui.jaRespondeu : ui.registradas}
              </p>
            </div>
          )}

        </div>

        <p className="ps__rodape">
          Pesquisa realizada por
          <img src={logoAccess} alt="Access" />
        </p>
      </div>
    </main>
  );
}
